import React, { useState } from 'react';
import { Avatar, EmptyState, Input } from '@meddihub/ui';
import type { HostPatient } from '@meddihub/mzcr-ui';
import { useWorkplacePatientsQuery } from '../../../graphql/graphql';
import { useAppContext } from '../../../Services/AppContext';
import useDateFormatter from '../../../Hooks/useDateFormatter';
import useDebouncedValue from '../../../Hooks/useDebouncedValue';
import useTranslation from '../../../Hooks/useTranslation';
import {
	useLazyGetPatientRidQuery,
	useLookupPatientRidMutation,
} from '../Services/mzcrApi';
import { withoutTitles } from '../Utils/patientName.util';
// @ts-ignore
import styles from './MzcrHostPatientSearch.module.scss';
import ListSkeleton from '../../Base/Components/ListSkeleton';
import { CheckIcon, LinkIcon, SearchIcon } from './icons';

interface Props {
	onSelect: (patient: HostPatient) => void;
}

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;
const SEARCH_RESULTS_COUNT = 10;

type Problem = { patientId: string; kind: 'notFound' | 'error' };

/**
 * "Z eKartotéky" tab of the MZČR patient picker (design G2) – searches the workplace's patients
 * and resolves the picked one's RID (stored one, or looked up in the MZČR registry). The pick is
 * confirmed by the modal's footer. The RID is looked for only for the picked patient.
 */
const MzcrHostPatientSearch: React.FC<Props> = ({ onSelect }) => {
	const [fulltext, setFulltext] = useState('');
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [resolvingId, setResolvingId] = useState<string | null>(null);
	const [pairedId, setPairedId] = useState<string | null>(null);
	const [problem, setProblem] = useState<Problem | null>(null);

	const { t } = useTranslation();
	const { userData } = useAppContext();
	const { formatDate } = useDateFormatter();
	const [getStoredRid] = useLazyGetPatientRidQuery();
	const [lookupRid] = useLookupPatientRidMutation();

	const search = useDebouncedValue(fulltext.trim(), SEARCH_DEBOUNCE_MS);
	const { data, isFetching } = useWorkplacePatientsQuery(
		{
			fulltext: search,
			workplaceIds: [userData.workplaceId],
			pagination: { take: SEARCH_RESULTS_COUNT },
		},
		{ skip: search.length < MIN_SEARCH_LENGTH },
	);
	const patients = (data?.workplacePatients?.data ?? [])
		.filter(Boolean)
		.map(member => member!.user!);

	const handleSelect = async (id: string, label: string) => {
		setSelectedId(id);
		setResolvingId(id);
		setPairedId(null);
		setProblem(null);
		try {
			let rid = (await getStoredRid(id).unwrap()).rid;
			if (!rid) {
				const result = await lookupRid(id).unwrap();
				rid = result.found ? result.rid : null;
			}
			if (rid) {
				setPairedId(id);
				onSelect({ rid, label, hostPatientId: id });
			} else {
				setProblem({ patientId: id, kind: 'notFound' });
			}
		} catch (e) {
			console.error(e);
			setProblem({ patientId: id, kind: 'error' });
		} finally {
			setResolvingId(null);
		}
	};

	const renderRidStatus = (id: string) => {
		if (resolvingId === id) {
			return (
				<span className={styles.rid}>
					<SearchIcon size={13} />
					{t('component.mzcr.hostPatientSearch.ridResolving')}
				</span>
			);
		}
		if (problem?.patientId === id) {
			return (
				<span className={`${styles.rid} ${styles.ridDanger}`}>
					{problem.kind === 'notFound'
						? t('component.mzcr.hostPatientSearch.ridNotFound')
						: t('component.mzcr.hostPatientSearch.ridFailed')}
				</span>
			);
		}
		if (pairedId === id) {
			return (
				<span className={`${styles.rid} ${styles.ridPaired}`}>
					<LinkIcon />
					{t('component.mzcr.hostPatientSearch.ridPaired')}
				</span>
			);
		}
		return null;
	};

	return (
		<div className={styles.root}>
			<div className={styles.search}>
				<Input
					aria-label={t('component.mzcr.hostPatientSearch.searchLabel')}
					placeholder={t('component.mzcr.hostPatientSearch.searchPlaceholder')}
					autoFocus
					prefix={<SearchIcon />}
					value={fulltext}
					onChange={event => setFulltext(event.target.value)}
				/>
			</div>
			<p className={styles.hint}>
				{t('component.mzcr.hostPatientSearch.hint')}
			</p>
			{search.length < MIN_SEARCH_LENGTH ? null : isFetching ? (
				<ListSkeleton rows={3} />
			) : patients.length === 0 ? (
				<EmptyState title={t('component.mzcr.hostPatientSearch.emptyTitle')}>
					{t('component.mzcr.hostPatientSearch.emptyText')}
				</EmptyState>
			) : (
				<ul className={styles.list}>
					{patients.map(patient => {
						const name = patient.profile?.fullName ?? '';
						const birthDate =
							patient.profile?.birthDate &&
							formatDate(patient.profile.birthDate);
						const isSelected =
							selectedId === patient.id && problem?.patientId !== patient.id;
						const details = [
							birthDate,
							patient.profile?.formattedBirthNumber &&
								t('component.mzcr.hostPatientSearch.birthNumber', {
									number: patient.profile.formattedBirthNumber,
								}),
						]
							.filter(Boolean)
							.join(' · ');
						return (
							<li key={patient.id}>
								<button
									type='button'
									className={`${styles.patient}${
										isSelected ? ` ${styles.selected}` : ''
									}`}
									aria-pressed={isSelected}
									disabled={resolvingId !== null}
									onClick={() =>
										// "Ing. Alena Vzorová · 12. 04. 1975" in the filter (design G3)
										handleSelect(
											patient.id,
											[name, birthDate].filter(Boolean).join(' · '),
										)
									}>
									<Avatar name={withoutTitles(name)} />
									<span className={styles.texts}>
										<span className={styles.name}>{name}</span>
										<span className={styles.meta}>{details}</span>
									</span>
									{renderRidStatus(patient.id)}
									{isSelected && resolvingId !== patient.id && (
										<span className={styles.check}>
											<CheckIcon />
										</span>
									)}
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

export default MzcrHostPatientSearch;
